import React, { useEffect, useMemo } from 'react'
import { Button, Grid, LinearProgress } from '@material-ui/core'
import { useDraftItemsStyles } from './draft-items-page.component.styles'
import { useTranslation } from 'react-i18next'
import { QueryParams } from '../home-page/home-component/home.constants'
import {
  getDraftItems,
  resetRequestParams,
  setQueryParams,
  setSortingInitial,
} from '../../store/draft-items/reducer'
import { useDispatch } from 'react-redux'
import { Filter } from '../../shared/filter'
import { SortByDateSelect } from '../../shared/sort-by-date-select'
import { ShowItemsSelect } from '../../components/show-items-select'
import { EmptySearchResultComponent } from '../../components/empty-search-result-component'
import { PlaceholderArrayComponent } from '../../components/placeholder-array-component'
import { ListedItem, ListedItemProps } from '../../components/listed-item'
import { AppPagination } from '../../components/app-pagination'
import { useNavigate } from 'react-router-dom'
import { useAppDialog } from '../../hooks/use-app-dialog'
import { useDraftItemsPage } from './draft-items-page.component.hook'
import { useScreenSize } from '../../hooks/use-screen-size'
import { ScreenSize } from '../../settings/device'
import { PageWrapper } from '../../components/page-wrapper'
import { BreadcrumbItem } from '../../components/breadcrumbs'
import { GridItem } from '../../shared/grid-item'

export const DraftItemsPage: React.FC = () => {
  const { t } = useTranslation()
  const classes = useDraftItemsStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const screenSize = useScreenSize()
  const { setAppDialog } = useAppDialog()

  const {
    //props
    itemsList,
    loading,
    query,
    totalPages,
    needApply,
    firstRender,
    isFilterCollapsed,
    userStatus,
    error,

    //methods
    changeFilterCollapsedAction,
  } = useDraftItemsPage()

  const draftBreadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('header.menu.search'), to: '/search' },
      { label: t('draftItemsPage.title') },
    ],
    [t]
  )

  //Get Listed Items
  useEffect(() => {
    const { pageSize, searchText, sortByDate, pageIndex, listedItemState } =
      query

    needApply &&
      dispatch(
        getDraftItems({
          pageSize,
          searchText,
          sortByDate,
          pageIndex,
          listedItemState,
        })
      )
  }, [query, needApply])

  //After Unmount
  useEffect(() => {
    return () => {
      dispatch(resetRequestParams())
    }
  }, [])

  const onQueryParamsChanged = (
    data: QueryParams,
    isApply?: boolean,
    isStartPagingInBegin?: boolean
  ) => {
    dispatch(
      setQueryParams({
        ...data,
        listedItemState: 0,
        isApply,
        isStartPagingInBegin,
      })
    )
  }

  //Filter Collapse Handler
  const filterCollapseHandler = (value: boolean) => {
    dispatch(changeFilterCollapsedAction(value))
  }

  //Cancel Handler
  const cancelHandler = () => {
    dispatch(setSortingInitial())
  }

  //User Activate
  useEffect(() => {
    !userStatus &&
      setAppDialog({
        isShow: true,
        title: t('myListingPage.appDialog.title'),
        message: t('myListingPage.appDialog.message'),
        variant: 'warning',
        actions: (
          <Button onClick={() => navigate('/account/security')} color="primary">
            {t('myListingPage.appDialog.btnText')}
          </Button>
        ),
        closeHandler: () => navigate('/'),
      })
  }, [userStatus])

  /* Error Loading Handler */
  useEffect(() => {
    error &&
      setAppDialog({
        isShow: true,
        title: t('errors.unexpectedError.title'),
        message: t('errors.unexpectedError.message'),
        variant: 'error',
      })
  }, [error])

  //If user not activated
  if (!userStatus) {
    return <></>
  }

  return (
    <>
      <LinearProgress style={{ visibility: loading ? 'visible' : 'hidden' }} />
      <PageWrapper
        title={t('draftItemsPage.title')}
        displayGoBackButton={false}
        breadcrumbItems={draftBreadcrumbItems}
        titleEndContent={
          screenSize === ScreenSize.Desktop ? (
            <div className={classes.queryParamsHolder}>
              <SortByDateSelect
                value={query.sortByDate}
                onChange={(value) =>
                  onQueryParamsChanged({ sortByDate: value }, true, true)
                }
                disabled={loading}
                idSuffix="draft"
              />
              <ShowItemsSelect
                value={query.pageSize}
                onChange={(pageSize) =>
                  onQueryParamsChanged({ pageSize }, true, true)
                }
                disabled={loading}
                label={t('myListingPage.showItems')}
                idSuffix="draft"
              />
            </div>
          ) : undefined
        }
      >
        <Grid container spacing={1}>
          {screenSize === ScreenSize.Mobile && (
            <Grid item xs={12}>
              <div className={classes.dateBlockMobile}>
                <div className={classes.mobileFilterControl}>
                  <Filter
                    cancelHandler={cancelHandler}
                    categories={[]}
                    isFilterCollapsed={isFilterCollapsed}
                    loading={loading}
                    nonprofitCategoryId={query.nonprofitCategoryId}
                    nonprofitId={query.nonprofitId}
                    nonprofits={[]}
                    onFilterChanged={onQueryParamsChanged}
                    screenSize={screenSize}
                    searchText={query.searchText}
                    itemsState={query.listedItemState}
                    setFilterCollapsed={filterCollapseHandler}
                    settings={{
                      hideCategory: true,
                      hideNonprofit: true,
                    }}
                  />
                </div>
                <SortByDateSelect
                  value={query.sortByDate}
                  onChange={(value) =>
                    onQueryParamsChanged({ sortByDate: value }, true, true)
                  }
                  disabled={loading}
                  idSuffix="draft-mobile"
                  className={classes.mobileSortSelect}
                />
                <ShowItemsSelect
                  value={query.pageSize}
                  onChange={(pageSize) =>
                    onQueryParamsChanged({ pageSize }, true, true)
                  }
                  disabled={loading}
                  label={t('myListingPage.showItems')}
                  idSuffix="draft-mobile"
                  className={classes.mobileShowItemsSelect}
                />
              </div>
            </Grid>
          )}

          {screenSize === ScreenSize.Desktop && (
            <Grid item xs={12} lg={3} xl={2} md={3}>
              <Filter
                cancelHandler={cancelHandler}
                categories={[]}
                isFilterCollapsed={isFilterCollapsed}
                loading={loading}
                nonprofitCategoryId={query.nonprofitCategoryId}
                nonprofitId={query.nonprofitId}
                nonprofits={[]}
                onFilterChanged={onQueryParamsChanged}
                screenSize={screenSize}
                searchText={query.searchText}
                itemsState={query.listedItemState}
                setFilterCollapsed={filterCollapseHandler}
                settings={{
                  hideCategory: true,
                  hideNonprofit: true,
                }}
              />
            </Grid>
          )}

          <Grid item xs={12} xl={10} lg={9} md={9}>
            <Grid container spacing={1}>
              {firstRender && <PlaceholderArrayComponent />}
              {!itemsList.length && !firstRender && (
                <Grid xs={12} item>
                  <EmptySearchResultComponent text="No draft items" />
                </Grid>
              )}
              {itemsList.map((item: ListedItemProps, key: number) => (
                <GridItem
                  key={key}
                  xxxs={12}
                  xxs={6}
                  xs={4}
                  md={4}
                  lg={3}
                  xl={2}
                >
                  <ListedItem
                    {...item}
                    itemUrl={`/my-listed-items/${item.id}`}
                    settings={{
                      hideCity: true,
                      hideRegion: true,
                      hideCondition: true,
                    }}
                    showDate
                  />
                </GridItem>
              ))}

              {/*Pagination*/}
              {!firstRender && totalPages > 1 && (
                <Grid item xs={12}>
                  <AppPagination
                    count={totalPages}
                    disabled={loading}
                    page={query.pageIndex + 1}
                    onChange={(_, page) =>
                      onQueryParamsChanged({ pageIndex: page - 1 }, true)
                    }
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </PageWrapper>
    </>
  )
}
